import React from "react";
import { Star, BicepsFlexed, Gauge, Clover, Megaphone, Sparkles, Hourglass, Dog } from "lucide-react";

export const STAT_META = {
  power:    { icon: BicepsFlexed, color: "#FF007F", short: "POW" },
  workrate: { icon: Gauge,        color: "#00F0FF", short: "WRK" },
  fortune:  { icon: Clover,       color: "#FFD700", short: "FOR" },
  pop:      { icon: Megaphone,    color: "#B44DFF", short: "POP" },
  xp_bonus: { icon: Sparkles,     color: "#39FF14", short: "XP", suffix: "%" },
  xp_bonus_pct: { icon: Sparkles, color: "#39FF14", short: "XP", suffix: "%", label: "XP Gain" },
  power_pct:    { icon: BicepsFlexed, color: "#FF007F", short: "POW", suffix: "%", label: "Power" },
  workrate_pct: { icon: Gauge,        color: "#00F0FF", short: "WRK", suffix: "%", label: "Workrate" },
  fortune_pct:  { icon: Clover,       color: "#FFD700", short: "FOR", suffix: "%", label: "Fortune" },
  pop_pct:      { icon: Megaphone,    color: "#B44DFF", short: "POP", suffix: "%", label: "Pop" },
  passive_gains_pct:   { icon: Hourglass, color: "#7DF9FF", short: "PASSIVE", suffix: "%", label: "Passive Gains" },
  training_hotdog_pct: { icon: Dog,       color: "#FF9500", short: "TRAIN HOT DOGS", suffix: "%", label: "Training Hot Dogs" },
  tour_fans_pct:   { icon: Megaphone, color: "#FF9500", short: "TOUR FANS", suffix: "%", label: "Tour Fans" },
  pyro_energy_pct: { icon: Sparkles,  color: "#FF3399", short: "PYRO ENERGY", suffix: "%", label: "Pyro Energy" },
  underdog_bonus:  { icon: Star,      color: "#00F0FF", short: "UNDERDOG", suffix: "%", label: "Underdog Bonus" },
  passive_gains:   { icon: Hourglass, color: "#7DF9FF", short: "PASSIVE", suffix: "%", label: "Passive Gains" },
  all:      { icon: Star,         color: "#FFE600", short: "ALL" },
};

const STAT_LABEL = { power: "Power", workrate: "Workrate", fortune: "Fortune", pop: "Pop", xp_bonus: "XP Bonus" };
export function bonusText(stat, value) {
  const m = STAT_META[stat] || {};
  return `+${Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}${m.suffix || ""} ${m.label || STAT_LABEL[stat] || stat}`;
}

export function StarRow({ tier, max = 4, size = 10 }) {
  return (
    <div className="flex gap-[1px]" data-testid="star-row">
      {Array.from({ length: max }, (_, k) => k + 1).map((i) => (
        <Star key={i} size={size}
              fill={i <= tier ? (i > 4 ? "#FF3399" : "#FFE600") : "transparent"}
              color={i <= tier ? (i > 4 ? "#FF3399" : "#FFE600") : "#695D8E"}
              style={i <= tier ? { filter: `drop-shadow(0 0 3px ${i > 4 ? "#FF3399" : "#FFE600"})` } : {}} />
      ))}
    </div>
  );
}

export function MiniChip({ icon: Icon, color, children, testId, title, dim }) {
  return (
    <div data-testid={testId} title={title}
         className="flex items-center gap-1 px-1.5 py-[3px] rounded-md bg-[#0A051A]/85 border min-w-0"
         style={{ borderColor: dim ? "#2D1C52" : `${color}55`, color: dim ? "#695D8E" : color }}>
      {Icon && <Icon size={10} strokeWidth={2.6} className="shrink-0" />}
      <span className="lb-num font-bold text-[10px] leading-none truncate">{children}</span>
    </div>
  );
}

export function StatChip({ stat, value, suffix, testId, dim }) {
  const m = STAT_META[stat] || STAT_META.all;
  return <MiniChip icon={m.icon} color={m.color} testId={testId} dim={dim} title={bonusText(stat, value)}>+{value}{suffix ?? m.suffix ?? ""} {m.short}</MiniChip>;
}

export function VaultTab({ id, icon: Icon, label, count, active, onClick, color }) {
  return (
    <button data-testid={`vault-tab-${id}`} onClick={onClick}
            className={`group flex items-center gap-2 px-5 py-2.5 rounded-full font-heading uppercase tracking-widest text-xs border-2 transition-[transform,background-color,box-shadow,color,border-color] duration-200 hover:-translate-y-0.5
              ${active ? "text-white" : "bg-[#0A051A]/85 backdrop-blur text-[#A89BCC] border-[#2D1C52] hover:border-[#00F0FF] hover:text-white"}`}
            style={active ? { background: color, borderColor: color, boxShadow: `0 0 20px ${color}AA`, color: color === "#00F0FF" || color === "#FFE600" || color === "#39FF14" ? "#0A051A" : "#fff" } : undefined}>
      <Icon size={15} className="transition-transform group-hover:rotate-12" /> {label}
      <span className="lb-num opacity-80">({count})</span>
    </button>
  );
}

export function FilterPill({ id, label, count, active, onClick, color = "#FF007F" }) {
  return (
    <button data-testid={`filter-${id}`} onClick={onClick}
            className={`px-3.5 py-1.5 rounded-full text-xs font-heading uppercase tracking-widest border-2 transition-[background-color,box-shadow,color,border-color] duration-200
              ${active ? "text-white" : "bg-[#0A051A]/85 text-[#A89BCC] border-[#2D1C52] hover:border-[#00F0FF] hover:text-white"}`}
            style={active ? { background: color, borderColor: color, boxShadow: `0 0 14px ${color}AA` } : undefined}>
      {label} <span className="lb-num opacity-80 ml-1">({count})</span>
    </button>
  );
}

export function TierScale({ max }) {
  const steps = Array.from({ length: max }, (_, i) => (10 ** i).toLocaleString());
  return <span className="neon-yellow lb-num font-bold" data-testid="tier-scale">{steps.join(" → ")}</span>;
}
